/**
 * useVerificationStatus hook — polls job status and subscribes to WebSocket.
 * callLog is persisted to localStorage so it survives tab switches and refreshes.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { verifyApi } from '../services/api';
import { JobStatusResponse, ProgressUpdate, CallLogEntry } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const WS_BASE = API_BASE_URL ? API_BASE_URL.replace(/^http/, 'ws') : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`;

function logStorageKey(jobId: string) {
  return `callLog:${jobId}`;
}

function loadLog(jobId: string): CallLogEntry[] {
  try {
    const raw = localStorage.getItem(logStorageKey(jobId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLog(jobId: string, entries: CallLogEntry[]) {
  try {
    localStorage.setItem(logStorageKey(jobId), JSON.stringify(entries.slice(0, 200)));
  } catch {}
}

function buildCallTracking(entries: CallLogEntry[]) {
  const callSidToPhone = new Map<string, string>();
  const inFlight = new Map<string, CallLogEntry>();

  for (const entry of [...entries].reverse()) {
    if (entry.call_sid && entry.phone_number) {
      callSidToPhone.set(entry.call_sid, entry.phone_number);
    }
    if (entry.type === 'call_started' && entry.call_sid) {
      inFlight.set(entry.call_sid, entry);
    }
    if (entry.type === 'call_completed' && entry.call_sid) {
      inFlight.delete(entry.call_sid);
    }
  }

  return { callSidToPhone, inFlight };
}

export function useVerificationStatus(jobId: string | null) {
  const [status, setStatus] = useState<JobStatusResponse | null>(null);
  const [progress, setProgress] = useState<ProgressUpdate | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed callLog from localStorage immediately so it survives tab switches
  const [callLog, setCallLog] = useState<CallLogEntry[]>(() =>
    jobId ? loadLog(jobId) : []
  );
  const [inFlightCalls, setInFlightCalls] = useState<Map<string, CallLogEntry>>(new Map());
  const callSidPhoneMapRef = useRef<Map<string, string>>(new Map());
  const wsRef = useRef<WebSocket | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const isComplete = status?.status === 'completed' || status?.status === 'failed' || status?.status === 'cancelled';

  const fetchStatus = useCallback(async () => {
    if (!jobId) return;
    try {
      const s = await verifyApi.getJobStatus(jobId);
      setStatus(s);
      // Stop polling the moment we see a terminal state — regardless of how
      // the interval was started (ws.onerror, ws.onclose, or anywhere else).
      const terminal = s.status === 'completed' || s.status === 'failed' || s.status === 'cancelled';
      if (terminal && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch status');
    }
  }, [jobId]);

  const addLogEntry = useCallback((entry: CallLogEntry) => {
    setCallLog(prev => {
      const next = [entry, ...prev].slice(0, 200);
      if (jobId) saveLog(jobId, next);
      return next;
    });
  }, [jobId]);

  // Persist whenever callLog changes (covers initial seed too)
  useEffect(() => {
    if (jobId && callLog.length > 0) saveLog(jobId, callLog);
  }, [jobId, callLog]);

  useEffect(() => {
    if (!jobId) return;

    // Reload from storage in case another tab wrote entries
    const storedLog = loadLog(jobId);
    const tracking = buildCallTracking(storedLog);
    setCallLog(storedLog);
    setInFlightCalls(tracking.inFlight);
    callSidPhoneMapRef.current = tracking.callSidToPhone;
    fetchStatus();

    const token = localStorage.getItem('auth_token') || '';
    const ws = new WebSocket(`${WS_BASE}/api/ws/${jobId}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Standard progress update
        if (data.current !== undefined) {
          setProgress(data as ProgressUpdate);
        }

        // Call started — AMD timer now running
        if (data.type === 'call_started') {
          const entry: CallLogEntry = {
            type: 'call_started',
            call_sid: data.call_sid,
            phone_number: data.phone_number,
            timestamp: data.timestamp || new Date().toISOString(),
          };
          if (data.call_sid && data.phone_number) {
            callSidPhoneMapRef.current.set(data.call_sid, data.phone_number);
          }
          setInFlightCalls(prev => {
            const next = new Map(prev);
            next.set(data.call_sid, entry);
            return next;
          });
        }

        // Call completed (progress update with category)
        if (data.current !== undefined && data.current_number && data.current_status) {
          const completedSid =
            data.call_sid ||
            Array.from(callSidPhoneMapRef.current.entries()).find(([, phone]) => phone === data.current_number)?.[0] ||
            null;
          if (completedSid) {
            callSidPhoneMapRef.current.set(completedSid, data.current_number);
          }
          const entry: CallLogEntry = {
            type: 'call_completed',
            call_sid: completedSid,
            phone_number: data.current_number,
            category: data.current_status,
            timestamp: new Date().toISOString(),
            ai_queued: ['UNKNOWN', 'VOICEMAIL', 'SWITCHBOARD'].includes(data.current_status),
          };
          addLogEntry(entry);
          setInFlightCalls(prev => {
            const next = new Map(prev);
            if (completedSid && next.has(completedSid)) {
              next.delete(completedSid);
            } else {
              for (const [sid, e] of next.entries()) {
                if (e.phone_number === data.current_number) {
                  next.delete(sid);
                  break;
                }
              }
            }
            return next;
          });
        }

        // Analysis update — look up phone number from the inFlight map before writing log
        if (data.type === 'analysis_update') {
          const entry: CallLogEntry = {
            type: 'analysis_update',
            call_sid: data.call_sid,
            phone_number: callSidPhoneMapRef.current.get(data.call_sid) || data.phone_number || data.call_sid || '',
            category: data.category,
            analysis_status: data.analysis_status,
            timestamp: new Date().toISOString(),
          };
          addLogEntry(entry);
        }

        if (data.status) {
          fetchStatus();
        }
      } catch {}
    };

    ws.onerror = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(fetchStatus, 2000);
    };

    ws.onclose = () => {
      // A clean close (nginx/idle timeout) won't fire onerror, so start the
      // polling fallback here too — otherwise a job that completes (e.g. via
      // the reconciler) after the socket drops would never update the UI.
      // fetchStatus() itself clears the interval once it sees a terminal state.
      fetchStatus();
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchStatus, 2000);
      }
    };

    return () => {
      ws.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId, fetchStatus, addLogEntry]);

  useEffect(() => {
    if (isComplete && pollRef.current) clearInterval(pollRef.current);
  }, [isComplete]);

  return { status, progress, error, isComplete, callLog, inFlightCalls, refetch: fetchStatus };
}
